/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';

import Analytics from 'arbitrip-common/client/analytics';

// Shared with the dev "Table view" / "Cards view" panels — one implementation of the board
// precedence, refundability, price formatting and room-details resolution.
import {
    BOARD_PRECEDENCE,
    KEY_ROOM_ONLY,
    formatPrice,
    isNonRefundable,
    refundableTill,
    roomDataFor,
} from './dealViewModel/dealViewModel';

const UNGROUPED_KEY = '__ungrouped__';
const UNGROUPED_LABEL = '(no room group)';
const UNNAMED_ROOM = '(no description)';

const KEY_NON_REFUNDABLE = 'NonRefundable';
const KEY_REFUNDABLE = 'FreeCancellation';

function boardLabel(deal, t) {
    const hit = BOARD_PRECEDENCE.find(([path]) => _.get(deal, path));
    return t(hit ? hit[1] : KEY_ROOM_ONLY);
}

function cancellationLabel(deal, t) {
    if (isNonRefundable(deal)) {
        return t(KEY_NON_REFUNDABLE);
    }
    const till = refundableTill(deal);
    if (!till) {
        return t(KEY_REFUNDABLE);
    }
    const parsed = new Date(till);
    return Number.isNaN(parsed.getTime())
        ? t(KEY_REFUNDABLE)
        : `${t(KEY_REFUNDABLE)} ${parsed.toISOString().slice(0, 10)}`;
}

/**
 * AN OPTION IS A `superseded_group_id`, NOT A COMBINATION THIS FILE INVENTS.
 *
 * `Hotel.computeSupersededGroupId()` already keys a deal by exactly the axes a guest chooses
 * between — `room_group | breakfast | half | full | all-inclusive | refundable-till` — and that key
 * is what decides which deals hide each other. Re-deriving "options" here from board × refundability
 * would be a second opinion about the same question, free to drift from the one that governs the
 * page.
 *
 * The basis is the NON-SUPERSEDED deals: a superseded deal is, by definition, one a better deal with
 * the same option already covers, so counting it would double-count an option.
 *
 * BUT `markSupersededDeals()` RETURNS EARLY WHEN ROOM MAPPING IS ON (`Hotel.js:840`), so on a
 * room-mapped hotel — which is every hotel this dev tool exists to inspect — nothing is ever marked
 * and every deal survives. `superseded_group_id` is still stamped (`:838` runs before that return),
 * which is why the id, not the flag, is the thing to group by. Falling back to the flag alone would
 * silently report "one option per deal" on exactly the hotels being tested.
 */
function optionsOf(deals, t) {
    const survivors = deals.filter((deal) => !deal.superseded);
    const basis = survivors.length > 0 ? survivors : deals;
    const byOption = _.groupBy(
        basis,
        (deal) => deal.superseded_group_id || `${boardLabel(deal, t)}|${cancellationLabel(deal, t)}`,
    );

    return Object.values(byOption)
        .map((optionDeals) => ({
            board: boardLabel(optionDeals[0], t),
            cancellation: cancellationLabel(optionDeals[0], t),
            deals: optionDeals.length,
            // `deals` stays a COUNT — the tooltip and the group list both read it as one. The member
            // array is a separate field so the click handler can log what it actually selected.
            members: optionDeals,
            cheapest: _.minBy(optionDeals, perNight),
        }))
        .sort((a, b) => perNight(a.cheapest) - perNight(b.cheapest));
}

/**
 * The hover text on the options chip: one line per option, so the combination that produced the
 * count is readable without expanding the group.
 */
function optionsTooltip(options, profile) {
    return options
        .map(
            (option) =>
                `${option.board} · ${option.cancellation} — ${option.deals} × ${formatPrice(
                    perNight(option.cheapest),
                    profile,
                )}`,
        )
        .join('\n');
}

/**
 * THE CARD LAYER — `dca.room_display_group` + `dca.room_options`, keyed by `card_id`.
 *
 * Distinct from `optionsOf` above and deliberately beside it, not instead of it: that one is
 * board × refundability (`superseded_group_id`), this one is the engine's identity axes (view,
 * amenity access, bed, balcony). §2.2 keeps them separate vocabularies, and `-B` §15.5 forbids
 * conflating them.
 *
 * PRESENCE, NOT A DEFAULT (`RULINGS_2026-08-28-B` RO-10). `room_options` ABSENT means the engine
 * did not run; PRESENT AND EMPTY means it ran and this card states nothing. A
 * `_.get(deal, 'room_options', [])` would collapse those two and report "no options" on every
 * unmapped deal, so the container is tested with `_.has` and the two states render differently.
 *
 * NEVER BRANCH ON `marker_state` TO DECIDE WHETHER TO RENDER OPTIONS. `markerStateOf` returns
 * `no_options` for ANY single-card display group BEFORE it looks at that card's own records, so a
 * card can read `no_options` while carrying a stated bed — measured at three of the seven cards
 * that gained one. Gating the list on the marker would hide that claim from the page. The marker is
 * shown beside the options as information, never as the gate on them.
 */
/**
 * THE CARD SUBTITLE — NOT THE FIELD NAME IT MIGHT LOOK LIKE. The card's own fine-group name is
 * `dca.room_group_name`, but that field never reaches this deal object: every variant of the wire
 * pick-list (`firebase_deal_availability_fields` and its two `.concat()`s in
 * `packages/transformer/transform-results-to-client.js`) has `'dca.room_group_name'` commented out,
 * so nothing ever flattens it here — confirmed by grep, not assumed.
 *
 * `dca.room_group_description` IS on the wire and IS already flattened onto this deal
 * (`packages/common/client/entities/Deal.js:157`), and on this branch it carries the same string:
 * the RM3 engine writes `room_group_description` and `room_group_name` from the one
 * `group.display_name` (`packages/room-mapping-3.0/src/adapters/dca-writer.ts:437-438`), and RM3 is
 * the engine every deal takes on this branch (`packages/recheck-manager/index.js`'s
 * `useRoomMapping3 || true`, see root `CLAUDE.md`). The legacy Gimmonix grouper writes these two
 * fields to DIFFERENT strings (`packages/deals-grouper/gimmonix_deals_grouper.js:468-469`), so this
 * stand-in is an RM3-branch equivalence, not a general one — flagged here for whoever revisits it
 * once the debug override is resolved.
 *
 * `Deal.js:157` also `.toLowerCase()`s the field and defaults an absent value to its own sentinel
 * `'UN'` (`Deal.js:19`) rather than leaving it undefined, so an absent group description arrives here
 * as the literal string `'un'` — filtered out below so an unmapped card never shows that as a
 * subtitle.
 */
const ROOM_GROUP_DESCRIPTION_ABSENT_SENTINEL = 'un';

function cardSubtitleOf(cheapest, groupName) {
    const trimmed = ((cheapest && cheapest.room_group_description) || '').trim();
    if (!trimmed || trimmed === ROOM_GROUP_DESCRIPTION_ABSENT_SENTINEL) {
        return null;
    }
    // A subtitle equal to the card's own group heading is noise, not information — same rule the
    // manager review page applies (`packages/room-mapping-3.0/test/harness/manager-review-page.ts`,
    // `cardSubtitleOf`'s "equals the heading" fold).
    if (trimmed === ((groupName || '').trim().toLowerCase())) {
        return null;
    }
    return trimmed;
}

function cardsOf(deals, t, groupName) {
    const byCard = _.groupBy(
        deals.filter((deal) => _.get(deal, 'room_display_group.card_id')),
        (deal) => deal.room_display_group.card_id,
    );

    return Object.entries(byCard)
        .map(([cardId, cardDeals]) => {
            const cheapest = _.minBy(cardDeals, perNight);

            return {
                cardId,
                deals: cardDeals,
                cheapest,
                markerState: _.get(cheapest, 'room_display_group.marker_state'),
                // Read off the deal we display AND log, so the three cannot disagree.
                optionsPresent: _.has(cheapest, 'room_options'),
                options: _.has(cheapest, 'room_options') ? cheapest.room_options : [],
                subtitle: cardSubtitleOf(cheapest, groupName),
                // THE BOOKABLE VARIATIONS WITHIN THIS CARD. Same basis as the group-level list —
                // `superseded_group_id`, per `optionsOf`'s docblock — so the two cannot drift. That
                // key is `room_group | board | refundable-till`, and a card is exactly one fine
                // group, so within a card it varies by board and refundability alone.
                variations: optionsOf(cardDeals, t),
            };
        })
        .sort((a, b) => perNight(a.cheapest) - perNight(b.cheapest));
}

/**
 * The click handler's whole purpose: put the deal behind a row on the console so it can be
 * inspected. Matches `DealComponent.react.js:297`'s existing `console.log('deal: ', deal)` shape.
 * The members go out beside it so a row that stands for several deals says which.
 */
function logDeal(label, deal, members) {
    console.log(`${CARD_LOG_PREFIX}${label}`, deal);
    if (members && members.length > 1) {
        console.log(`${CARD_LOG_PREFIX}${label} — all ${members.length} deals`, members);
    }
}

/**
 * THE PANEL GROUPS BY THE **DISPLAY** GROUP, NOT THE FINE GROUP.
 *
 * `dca.room_group` is the FINE partition — one key per veto-equivalence class, and the thing
 * `compareSuperseded` keys on. `dca.room_display_group.key` is the COARSE partition: the same rooms
 * with `view`, `access` and `balcony` dropped and `bed` replaced by capacity, which is the layer a
 * guest is meant to see as ONE room with several cards.
 *
 * Grouping by the fine key made this panel structurally unable to show the feature it exists to
 * inspect: every group rendered exactly one card, because a fine group IS one card by construction.
 * Five `junior suite …` rows that the engine had correctly merged into a single display group read
 * as five separate rooms.
 *
 * A deal with NO display group is not folded into a neighbour — it keeps its fine key under a
 * distinct prefix and is labelled, because absent means the engine did not run for that deal and
 * that is a different statement from "it stands alone".
 */
const NO_DISPLAY_GROUP_PREFIX = 'nodg:';

function displayGroupKeyOf(deal) {
    const key = _.get(deal, 'room_display_group.key');
    return key || `${NO_DISPLAY_GROUP_PREFIX}${deal.room_group || UNGROUPED_KEY}`;
}

function perNight(deal) {
    const value = _.get(deal, 'pricePerNight');
    return _.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

const DETAILS_STATE_ON_CHEAPEST = 'on_cheapest';
const DETAILS_STATE_ELSEWHERE = 'elsewhere';
const DETAILS_STATE_NONE = 'none';

const RD_PREFIX = 'RD: ';
const CATALOG_PREFIX = '(Catalog) ';
// `generic` is a family bucket and `run_of_house` its own category — neither is an Expedia catalog room.
const CATALOG_MATCH_METHODS = ['declared', 'confirmed', 'sticky'];
const MATCH_METHOD_PATH = 'chooseprod.dca.room_mapping_meta.match_method';
const CATALOG_HINT =
    'This container is an Expedia catalog room, not a generic family bucket — dca.room_mapping_meta.match_method: ';
const CARD_PREFIX = 'CARD: ';
const CARD_LOG_PREFIX = '[RoomGroups] card ';
const CARD_STATE_ENGINE_ABSENT = 'engine did not run';
const CARD_STATE_NO_OPTIONS = 'no options stated';
const CARD_CLICK_HINT = 'Click to console.log this card’s deal';
const MERGED_PREFIX = '⤳ ';
const MERGED_HINT = 'Fine room_groups merged into this display group';
const NO_DISPLAY_GROUP_LABEL = 'no display group';
const NO_DISPLAY_GROUP_HINT = 'dca.room_display_group is absent — the display layer did not run for these deals';

/**
 * A HOLLOW GREY DOT MEANS "NOBODY WROTE DETAILS", NOT "SOMETHING IS BROKEN".
 *
 * A group with no enrichment is a supported product state — the page renders the supplier's own
 * name and sells the room perfectly well. Painting that red would report a normal outcome as a
 * fault, and a panel that cries wolf on the common case stops being read. Red is reserved for
 * nothing here; amber is the one state that genuinely wants attention, because it means the
 * details exist in the group and the deal the button opens is not the one carrying them.
 */
const DETAILS_MARKERS = {
    [DETAILS_STATE_ON_CHEAPEST]: { fill: '#2e7d32', border: '#2e7d32', labelKey: 'RoomGroupsDetailsPresent' },
    [DETAILS_STATE_ELSEWHERE]: { fill: '#ef9a00', border: '#ef9a00', labelKey: 'RoomGroupsDetailsPartial' },
    [DETAILS_STATE_NONE]: { fill: 'transparent', border: '#bbb', labelKey: 'RoomGroupsDetailsAbsent' },
};

/**
 * Which deal in the group actually carries room details, and how many of them do.
 *
 * The deals arrive price-sorted, so index 0 is the one the group is priced on. Opening the dialog
 * on a deal that has no details would show an empty modal while a sibling deal in the same group
 * holds a full catalog room — so the button follows the details, and the marker reports whether it
 * had to look past the cheapest deal to find them.
 */
function detailsOf(deals, hotel) {
    const resolved = deals.map((deal) => ({ deal, room: roomDataFor(hotel, deal) }));
    const carriers = resolved.filter((entry) => entry.room !== null);

    if (carriers.length === 0) {
        return { state: DETAILS_STATE_NONE, deal: null, room: null, source: null, carriers: 0, name: null, roomId: null };
    }
    const first = carriers[0];
    return {
        state: resolved[0].room ? DETAILS_STATE_ON_CHEAPEST : DETAILS_STATE_ELSEWHERE,
        deal: first.deal,
        room: first.room.data,
        source: first.room.source,
        carriers: carriers.length,
        name: (first.room.data.name || '').trim() || null,
        roomId: first.room.data.id || null,
    };
}

/**
 * The name the room-details modal puts in its title bar.
 *
 * `RoomsCatalogDialog` titles itself `roomName ?? room.name`, and this panel passes the deal's own
 * description as `roomName` — which is already in the table below, so echoing it here would say
 * nothing. `room.name` is the half worth surfacing: it names the catalog room the group's details
 * actually resolved to, which is how you see at a glance that a group is wearing the wrong room's
 * pictures. The dialog itself exposes the same thing in dev mode, appended as `- Expedia: …`.
 */
function detailsNameTooltip(details) {
    const parts = [details.name];
    if (details.roomId) {
        parts.push(`(${details.roomId})`);
    }
    return `${RD_PREFIX}${parts.join(' ')}`;
}

function detailsTooltip(details, total, t) {
    if (details.state === DETAILS_STATE_NONE) {
        return `${t('RoomGroupsDetailsAbsent')} — 0/${total}`;
    }
    const headline =
        details.state === DETAILS_STATE_ON_CHEAPEST
            ? t('RoomGroupsDetailsPresent')
            : t('RoomGroupsDetailsPartial');
    return `${headline} — ${details.carriers}/${total}, ${t(details.source)}`;
}

function priceRange(deals, profile) {
    const prices = deals.map(perNight).filter(_.isFinite);
    if (prices.length === 0) {
        return '—';
    }
    const low = Math.min(...prices);
    const high = Math.max(...prices);
    return low === high ? formatPrice(low, profile) : `${formatPrice(low, profile)} – ${formatPrice(high, profile)}`;
}

/**
 * THE SUPPLIER'S OWN STRING, NOT THE ONE THE PLATFORM SETTLED ON.
 *
 * `deal.description` is already normalised, so a table built from it shows the engine agreeing with
 * itself — two rate lines that arrived under different supplier names collapse into one row and the
 * disagreement that made the group interesting disappears before it can be read.
 * `room_description_original` is what the supplier actually sent, which is the thing being judged.
 *
 * The deal row reads it from `original.details` (`DealComponent:362`) and analytics from `details`
 * (`dealService.js:53`); both shapes occur, so both are tried.
 *
 * When neither exists the row falls back to `description` and SAYS SO. Silently mixing a normalised
 * string into a column labelled "original" would put two vocabularies in one place and make the
 * table read as though every supplier happened to agree.
 */
function originalDescription(deal) {
    const original =
        _.get(deal, 'original.details.room_description_original') ||
        _.get(deal, 'details.room_description_original');
    const trimmed = (original || '').trim();
    if (trimmed) {
        return { text: trimmed, isOriginal: true };
    }
    return { text: (deal.description || '').trim() || UNNAMED_ROOM, isOriginal: false };
}

/**
 * One row per distinct supplier string, as the review page collapses them.
 *
 * `members` keeps the row's own deal objects so the card-select highlight joins on deal identity —
 * the same objects `cardsOf` groups — rather than re-deriving the description string on the other
 * side and risking the two derivations drifting apart. Every deal lands in exactly one row: a deal
 * with no original description still gets a row from the `description` fallback (`isOriginal:
 * false`), so a selection always has a row to land on.
 */
function collapseByDescription(deals) {
    const byDescription = _.groupBy(deals, (deal) => originalDescription(deal).text.toLowerCase());

    return Object.values(byDescription)
        .map((sameName) => {
            const cheapest = _.minBy(sameName, perNight);
            const label = originalDescription(cheapest);
            return {
                description: label.text,
                isOriginal: label.isOriginal,
                deals: sameName.length,
                members: sameName,
                suppliers: _.uniq(sameName.map((deal) => String(deal.id || '').split(':')[0])).sort(),
                superseded: sameName.filter((deal) => deal.superseded).length,
                low: perNight(cheapest),
                high: perNight(_.maxBy(sameName, perNight)),
                cheapest,
            };
        })
        .sort((a, b) => a.low - b.low);
}

const styles = {
    component: {
        border: '1px solid lightgrey',
        borderRadius: '4px',
        padding: '4px 4px 4px 15px',
        marginTop: '10px',
    },
    summary: { cursor: 'pointer', userSelect: 'none', fontWeight: 600, padding: '4px 0' },
    groupSummary: {
        cursor: 'pointer',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '5px 6px',
        borderTop: '1px solid #eee',
    },
    name: { flex: '1 1 auto', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' },
    meta: { flex: 'none', color: '#777', fontSize: '12px' },
    // Same weight as the deal count, but a catalog room name can be long — cap it and let the
    // group's own name keep the flexible space rather than being squeezed out by the annotation.
    rd: {
        flex: '0 1 auto',
        minWidth: 0,
        maxWidth: '38%',
        color: '#777',
        fontSize: '12px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        cursor: 'help',
    },
    catalog: { flex: 'none', color: '#777', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' },
    option: {
        flex: 'none',
        fontSize: '11px',
        border: '1px solid #cfd8dc',
        borderRadius: '10px',
        padding: '1px 7px',
        color: '#455a64',
        cursor: 'help',
    },
    price: { flex: 'none', fontWeight: 600, minWidth: '120px', textAlign: 'right' },
    table: { width: '100%', fontSize: '12px', borderCollapse: 'collapse', margin: '4px 0 2px 0' },
    th: { textAlign: 'left', color: '#777', fontWeight: 500, padding: '3px 6px', borderBottom: '1px solid #eee' },
    td: { padding: '3px 6px', borderBottom: '1px solid #f5f5f5', verticalAlign: 'top' },
    tdRight: { padding: '3px 6px', borderBottom: '1px solid #f5f5f5', textAlign: 'right', whiteSpace: 'nowrap' },
    footer: { padding: '4px 6px 10px 6px' },
    detailsButton: {
        background: 'none',
        border: 'none',
        padding: 0,
        color: '#1976d2',
        cursor: 'pointer',
        textDecoration: 'underline',
        fontSize: '12px',
    },
    cardList: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '10px', padding: '5px 6px 0 6px' },
    cardRow: {
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: '7px',
        width: '100%',
        background: '#fafafa',
        border: '1px solid #e0e0e0',
        borderRadius: '3px',
        padding: '3px 7px',
        fontSize: '11px',
        fontFamily: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
    },
    cardPrefix: { color: '#777', flex: 'none' },
    cardSubtitle: { fontWeight: 600 },
    cardOption: { color: '#1a237e', background: '#e8eaf6', borderRadius: '10px', padding: '1px 7px' },
    cardMarker: { color: '#999' },
    cardState: { color: '#b26500' },
    cardPrice: { marginLeft: 'auto', fontWeight: 600, whiteSpace: 'nowrap' },
    // Each card is a column in a wrapping row: the card button on top, its variations beneath.
    cardBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        flex: '1 1 300px',
        minWidth: '260px',
        maxWidth: '420px',
        padding: '6px 8px',
        boxSizing: 'border-box',
        background: '#f7f7f9',
        border: '1px solid #e3e3e8',
        borderRadius: '8px',
    },
    cardRowSelected: { background: '#fff3cd', borderColor: '#f0ad4e', boxShadow: '0 0 0 1px #f0ad4e' },
    variationRowSelected: { background: '#fff3cd', borderColor: '#f0ad4e', boxShadow: '0 0 0 1px #f0ad4e' },
    variationRow: {
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: '7px',
        width: 'calc(100% - 14px)',
        marginLeft: '14px',
        background: 'none',
        border: '1px solid #f0f0f0',
        borderRadius: '3px',
        padding: '2px 7px',
        fontSize: '11px',
        fontFamily: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
    },
    variationBoard: { color: '#455a64' },
    variationCancel: { color: '#6a1b9a' },
    merged: {
        flex: 'none',
        fontSize: '11px',
        background: '#e8f5e9',
        border: '1px solid #a5d6a7',
        borderRadius: '10px',
        padding: '1px 7px',
        color: '#2e7d32',
        cursor: 'help',
    },
    dot: {
        flex: 'none',
        width: '9px',
        height: '9px',
        borderRadius: '50%',
        border: '1px solid',
        cursor: 'help',
    },
    fallback: { color: '#b26500', cursor: 'help' },
    // The row holding the selection's cheapest deal vs. a row holding any other member of it —
    // same two-tone scheme as the review page's `deal-row--selected` / `deal-row--in-card`.
    rowSelected: { background: '#fff3cd', outline: '2px solid #f0ad4e', outlineOffset: '-2px' },
    rowInSelection: { background: '#fffbe6' },
    empty: { color: '#777', fontSize: '12px', padding: '6px' },
};

const SELECTION_KEY_SEPARATOR = '|';

function variationKeyOf(cardId, variation) {
    return [cardId, variation.board, variation.cancellation].join(SELECTION_KEY_SEPARATOR);
}

/**
 * Resolve the selected key back to the deals it stands for. The key is a card id or
 * `cardId|board|cancellation` for one of its variations; the members are the same deal objects the
 * table rows keep, so the join below is by identity, not by re-derived string.
 */
function selectionOf(cards, selected) {
    if (!selected) {
        return null;
    }
    for (const card of cards) {
        if (card.cardId === selected) {
            return { cheapest: card.cheapest, members: new Set(card.deals) };
        }
        const variation = card.variations.find((candidate) => variationKeyOf(card.cardId, candidate) === selected);
        if (variation) {
            return { cheapest: variation.cheapest, members: new Set(variation.members) };
        }
    }
    return null;
}

function rowSelectionStyle(row, selection) {
    if (!selection) {
        return null;
    }
    if (row.members.includes(selection.cheapest)) {
        return styles.rowSelected;
    }
    if (row.members.some((deal) => selection.members.has(deal))) {
        return styles.rowInSelection;
    }
    return null;
}

const RoomGroupRow = ({ group, profile, hotel, onRoomDetails, t }) => {
    const [open, setOpen] = useState(false);
    const rows = useMemo(() => (open ? collapseByDescription(group.deals) : []), [open, group.deals]);
    const cards = useMemo(
        () => (open ? cardsOf(group.deals, t, group.name) : []),
        [open, group.deals, t, group.name],
    );
    const details = useMemo(() => detailsOf(group.deals, hotel), [group.deals, hotel]);
    const marker = DETAILS_MARKERS[details.state];

    // A card id, or `cardId|board|cancellation` for a variation; `null` when nothing is selected.
    const [selected, setSelected] = useState(null);
    const selection = useMemo(() => selectionOf(cards, selected), [cards, selected]);
    const selectedRowRef = useRef(null);
    const toggleSelected = (key) => setSelected((current) => (current === key ? null : key));

    useEffect(() => {
        const row = selectedRowRef.current;
        if (selected && row && typeof row.scrollIntoView === 'function') {
            row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [selected]);

    return (
        <div>
            <div style={styles.groupSummary} onClick={() => setOpen(!open)}>
                <span style={{ flex: 'none', width: '10px' }}>{open ? '▾' : '▸'}</span>
                <span style={styles.name} title={group.name}>
                    {group.name}
                </span>
                {group.isCatalogContainer && (
                    <span style={styles.catalog} title={`${CATALOG_HINT}${group.matchMethod}`}>
                        {CATALOG_PREFIX}
                    </span>
                )}
                {details.name && (
                    <span style={styles.rd} title={detailsNameTooltip(details)}>
                        {RD_PREFIX}
                        {details.name}
                    </span>
                )}
                <span style={styles.meta}>
                    {group.deals.length} {t(group.deals.length === 1 ? 'RoomGroupsDeal' : 'RoomGroupsDeals')}
                </span>
                {group.fineGroups > 1 && (
                    <span style={styles.merged} title={MERGED_HINT}>
                        {MERGED_PREFIX}
                        {group.fineGroups}
                    </span>
                )}
                {!group.hasDisplayGroup && (
                    <span style={styles.cardState} title={NO_DISPLAY_GROUP_HINT}>
                        {NO_DISPLAY_GROUP_LABEL}
                    </span>
                )}
                <span style={styles.option} title={optionsTooltip(group.options, profile)}>
                    {group.options.length}{' '}
                    {t(group.options.length === 1 ? 'RoomGroupsOption' : 'RoomGroupsOptions')}
                </span>
                <span
                    style={{ ...styles.dot, background: marker.fill, borderColor: marker.border }}
                    title={detailsTooltip(details, group.deals.length, t)}
                    aria-label={t(marker.labelKey)}
                />
                <span style={styles.price}>{priceRange(group.deals, profile)}</span>
            </div>

            {open && (
                <div>
                    {cards.length > 0 && (
                        <div style={styles.cardList}>
                            {cards.map((card) => (
                                <div key={card.cardId} style={styles.cardBlock}>
                                <button
                                    type="button"
                                    style={
                                        selected === card.cardId
                                            ? { ...styles.cardRow, ...styles.cardRowSelected }
                                            : styles.cardRow
                                    }
                                    title={`${card.cardId}\n${CARD_CLICK_HINT}`}
                                    onClick={() => {
                                        logDeal(card.cardId, card.cheapest, card.deals);
                                        toggleSelected(card.cardId);
                                    }}
                                >
                                    <span style={styles.cardPrefix}>{CARD_PREFIX}</span>
                                    {card.subtitle && <span style={styles.cardSubtitle}>{card.subtitle}</span>}
                                    {!card.optionsPresent && (
                                        <span style={styles.cardState}>{CARD_STATE_ENGINE_ABSENT}</span>
                                    )}
                                    {card.optionsPresent && card.options.length === 0 && (
                                        <span style={styles.cardState}>{CARD_STATE_NO_OPTIONS}</span>
                                    )}
                                    {card.options.map((option) => (
                                        <span key={option.axis} style={styles.cardOption}>
                                            {option.axis}: {option.value}
                                        </span>
                                    ))}
                                    {card.markerState && <span style={styles.cardMarker}>{card.markerState}</span>}
                                    <span style={styles.cardMarker}>
                                        {card.deals.length}{' '}
                                        {t(card.deals.length === 1 ? 'RoomGroupsDeal' : 'RoomGroupsDeals')}
                                    </span>
                                    <span style={styles.cardPrice}>
                                        {formatPrice(perNight(card.cheapest), profile)}
                                    </span>
                                </button>
                                {card.variations.map((variation) => (
                                    <button
                                        key={`${variation.board}|${variation.cancellation}`}
                                        type="button"
                                        style={
                                            selected === variationKeyOf(card.cardId, variation)
                                                ? { ...styles.variationRow, ...styles.variationRowSelected }
                                                : styles.variationRow
                                        }
                                        title={`${card.cardId}\n${variation.board} · ${variation.cancellation}\n${CARD_CLICK_HINT}`}
                                        onClick={() => {
                                            logDeal(
                                                `${variation.board} · ${variation.cancellation}`,
                                                variation.cheapest,
                                                variation.members,
                                            );
                                            toggleSelected(variationKeyOf(card.cardId, variation));
                                        }}
                                    >
                                        <span style={styles.variationBoard}>{variation.board}</span>
                                        <span style={styles.cardMarker}>&middot;</span>
                                        <span style={styles.variationCancel}>{variation.cancellation}</span>
                                        <span style={styles.cardMarker}>
                                            {variation.deals}{' '}
                                            {t(variation.deals === 1 ? 'RoomGroupsDeal' : 'RoomGroupsDeals')}
                                        </span>
                                        <span style={styles.cardPrice}>
                                            {formatPrice(perNight(variation.cheapest), profile)}
                                        </span>
                                    </button>
                                ))}
                                </div>
                            ))}
                        </div>
                    )}
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>{t('RoomGroupsOriginalDescription')}</th>
                                <th style={styles.th}>{t('RoomGroupsSuppliers')}</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>#</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>{t('PerNight')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const rowStyle = rowSelectionStyle(row, selection);
                                return (
                                <tr
                                    key={row.description}
                                    style={rowStyle || undefined}
                                    ref={rowStyle === styles.rowSelected ? selectedRowRef : null}
                                >
                                    <td style={styles.td}>
                                        {row.description}
                                        {!row.isOriginal && (
                                            <span style={styles.fallback} title={t('RoomGroupsNoOriginalHint')}>
                                                {' '}
                                                {t('RoomGroupsNoOriginal')}
                                            </span>
                                        )}
                                        {row.superseded > 0 && (
                                            <span style={{ color: '#999' }}> &middot; {row.superseded} {t('Superseded')}</span>
                                        )}
                                    </td>
                                    <td style={styles.td}>{row.suppliers.join(', ')}</td>
                                    <td style={styles.tdRight}>{row.deals}</td>
                                    <td style={styles.tdRight}>
                                        {row.low === row.high
                                            ? formatPrice(row.low, profile)
                                            : `${formatPrice(row.low, profile)} – ${formatPrice(row.high, profile)}`}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div style={styles.footer}>
                        {details.room ? (
                            <button
                                type="button"
                                style={styles.detailsButton}
                                onClick={() =>
                                    onRoomDetails(
                                        details.room,
                                        details.deal.description,
                                        Analytics.actions.views.roomInfoDialog,
                                    )
                                }
                            >
                                {t('RoomDetails')}
                            </button>
                        ) : (
                            <span style={{ color: '#999', fontSize: '12px' }}>{t('RoomGroupsNoRoomDetails')}</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const RoomGroups = ({ deals, hotel, profile, onRoomDetails, t }) => {
    const [open, setOpen] = useState(false);

    const groups = useMemo(() => {
        if (!Array.isArray(deals) || deals.length === 0) {
            return [];
        }
        const byGroup = _.groupBy(deals, displayGroupKeyOf);

        return Object.entries(byGroup)
            .map(([key, groupDeals]) => {
                const sorted = [...groupDeals].sort((a, b) => perNight(a) - perNight(b));
                const displayLabel = _.get(sorted[0], 'room_display_group.label');
                const fineKeys = _.uniq(sorted.map((deal) => deal.room_group || UNGROUPED_KEY));
                const ungrouped = key === `${NO_DISPLAY_GROUP_PREFIX}${UNGROUPED_KEY}`;
                const withMatchMethod = sorted.find((deal) => _.get(deal, MATCH_METHOD_PATH) !== undefined);
                const matchMethod = withMatchMethod ? _.get(withMatchMethod, MATCH_METHOD_PATH) : null;

                return {
                    key,
                    // The display label is RO-4's own string with its own naming policy. Only when
                    // there is no display group does the fine group's description stand in.
                    name:
                        displayLabel ||
                        (sorted.find((deal) => deal.room_group_description) || {}).room_group_description ||
                        (ungrouped ? UNGROUPED_LABEL : sorted[0].description || UNNAMED_ROOM),
                    hasDisplayGroup: Boolean(displayLabel),
                    fineGroups: fineKeys.length,
                    deals: sorted,
                    options: optionsOf(sorted, t),
                    matchMethod,
                    isCatalogContainer: CATALOG_MATCH_METHODS.includes(matchMethod),
                };
            })
            .sort((a, b) => perNight(a.deals[0]) - perNight(b.deals[0]));
    }, [deals, t]);

    return (
        <div style={styles.component}>
            <div style={styles.summary} onClick={() => setOpen(!open)}>
                {open ? '▾' : '▸'} {t('RoomGroups')} ({groups.length})
            </div>
            {open &&
                (groups.length === 0 ? (
                    <div style={styles.empty}>{t('RoomGroupsNoDeals')}</div>
                ) : (
                    groups.map((group) => (
                        <RoomGroupRow
                            key={group.key}
                            group={group}
                            profile={profile}
                            hotel={hotel}
                            onRoomDetails={onRoomDetails}
                            t={t}
                        />
                    ))
                ))}
        </div>
    );
};

export { displayGroupKeyOf };

export default withTranslation()(RoomGroups);
